// ✅ src/pages/VehicleV2/VehicleSpareDeductionEntry.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/spareDeduction", spareDeduction);
//
// APIs used:
//   GET  https://nissi-india.com/api/spareDeduction/subtypes?mode=add|edit
//   GET  https://nissi-india.com/api/spareDeduction/view?spareId=123
//   POST https://nissi-india.com/api/spareDeduction/save
//
// Routing:
//   Add : /vehicle/VehicleSpareDeductionEntry
//   Edit: /vehicle/VehicleSpareDeductionEntry?sparedeductionperid=123
//
// ✅ Updated to match VehicleTdsPercentEntry pattern:
// - common localStorage/sessionStorage header pattern
// - add/edit mode supported
// - loading + pageLoading + loadingDDL states
// - same UI structure style
// - subtype locked in edit mode
// - back button goes to /vehicle/VehicleSpareDeductionListing

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/spareDeduction";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

// dd/MM/yyyy -> yyyy-MM-dd
const ddmmyyyyToYmd = (ddmmyyyy) => {
  const v = toStr(ddmmyyyy);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

// yyyy-MM-dd -> dd/MM/yyyy
const ymdToDDMMYYYY = (ymd) => {
  const v = toStr(ymd);
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const todayToYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const todayToDDMMYYYY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isValidDDMMYYYY = (s) => {
  const v = toStr(s);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);

  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const clampPercentage = (raw) => {
  let v = toStr(raw).replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;

  const [a, b] = v.split(".");
  if (typeof b === "string") v = `${a}.${b.slice(0, 2)}`;

  if (v.length > 6) v = v.slice(0, 6);
  return v;
};

const VehicleSpareDeductionEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const spareId = useMemo(() => {
    return toInt(
      searchParams.get("sparedeductionperid") ||
        searchParams.get("spareId") ||
        searchParams.get("id"),
      0
    );
  }, [searchParams]);

  const isEdit = spareId > 0;
  const todayYmd = useMemo(() => todayToYMD(), []);

  // dropdown
  const [subtypes, setSubtypes] = useState([]);

  // fields
  const [subtypeId, setSubtypeId] = useState("Select");
  const [percentage, setPercentage] = useState("");
  const [entryDate, setEntryDate] = useState(""); // dd/MM/yyyy

  // loading
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingDDL, setLoadingDDL] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  /* ------------------------- LOAD SUBTYPES ------------------------- */
  const loadSubtypes = useCallback(async () => {
    try {
      setLoadingDDL(true);

      const mode = isEdit ? "edit" : "add";

      const { data } = await axios.get(`${API_BASE}/subtypes`, {
        params: { mode },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load sub vehicle types");
      }

      const list = Array.isArray(data?.subtypes) ? data.subtypes : [];

      setSubtypes(
        [{ id: "Select", name: "Select" }].concat(
          list.map((x) => ({
            id: String(x.id),
            name: String(x.name),
          }))
        )
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load sub vehicle types");
      setSubtypes([{ id: "Select", name: "Select" }]);
    } finally {
      setLoadingDDL(false);
    }
  }, [isEdit, commonHeaders]);

  /* ------------------------- LOAD EDIT DATA ------------------------- */
  const loadEditData = useCallback(async () => {
    if (!isEdit) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { spareId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load record");
      }

      const r = data?.data || {};

      setSubtypeId(r.subtypeId != null ? String(r.subtypeId) : "Select");
      setPercentage(r.percentage != null ? String(r.percentage) : "");
      setEntryDate(toStr(r.entryDate));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load record");
    } finally {
      setPageLoading(false);
    }
  }, [isEdit, spareId, commonHeaders]);

  useEffect(() => {
    loadSubtypes();
  }, [loadSubtypes]);

  useEffect(() => {
    if (isEdit) {
      loadEditData();
    } else {
      setEntryDate(todayToDDMMYYYY());
    }
  }, [isEdit, loadEditData]);

  const canSave = useMemo(() => {
    if (String(subtypeId) === "Select") return false;
    if (!toStr(entryDate)) return false;
    if (!toStr(percentage)) return false;
    return true;
  }, [subtypeId, entryDate, percentage]);

  const validate = () => {
    if (!subtypeId || String(subtypeId) === "Select") {
      toast.error("Please select Vehicle Sub Type");
      return false;
    }

    if (!toStr(entryDate)) {
      toast.error("Please select Effect Date");
      return false;
    }

    if (!isValidDDMMYYYY(entryDate)) {
      toast.error("Invalid date format (dd/MM/yyyy)");
      return false;
    }

    const [dd, mm, yyyy] = entryDate.split("/").map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() > today.getTime()) {
      toast.error("Select Current Date or Previous date");
      return false;
    }

    if (!toStr(percentage)) {
      toast.error("Please enter Percentage");
      return false;
    }

    const pct = Number(percentage);
    if (!Number.isFinite(pct)) {
      toast.error("Percentage must be a number");
      return false;
    }

    if (pct <= 0) {
      toast.error("Percentage should be greater than 0");
      return false;
    }

    if (pct > 100) {
      toast.error("Percentage should not be greater than 100");
      return false;
    }

    return true;
  };

  const handleClear = () => {
    if (isEdit) {
      loadEditData();
      return;
    }

    setSubtypeId("Select");
    setPercentage("");
    setEntryDate(todayToDDMMYYYY());
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      spareId: spareId || 0,
      subtypeId: toInt(subtypeId, 0),
      entryDate: ddmmyyyyToYmd(entryDate), // send yyyy-MM-dd
      percentage: Number(percentage),
      loginUserId,
      branchId,
      branchType: branch,
    };

    try {
      setLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(
        data?.message ||
          (isEdit ? "Spare deduction updated successfully" : "Spare deduction saved successfully")
      );

      setTimeout(() => {
        navigate("/vehicle/VehicleSpareDeductionListing");
      }, 800);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SPARE DEDUCTION PERCENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? "Edit Spare Deduction Entry" : "Add Spare Deduction Entry"}
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isEdit
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isEdit ? `Edit Mode (#${spareId})` : "New Entry"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/VehicleSpareDeductionListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="max-w-2xl mx-auto">
              {/* Vehicle Sub Type */}
              <div className="mb-8">
                <label className={labelClass}>
                  Vehicle Sub Type <span className="text-red-500">*</span>
                </label>

                <select
                  value={subtypeId}
                  onChange={(e) => setSubtypeId(e.target.value)}
                  disabled={loadingDDL || loading || pageLoading || isEdit}
                  className={`${inputClass} ${loadingDDL ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {subtypes.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Percentage */}
              <div className="mb-8">
                <label className={labelClass}>
                  Percent <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  value={percentage}
                  onChange={(e) => setPercentage(clampPercentage(e.target.value))}
                  onBlur={() => setPercentage(clampPercentage(percentage))}
                  placeholder="Enter percentage..."
                  className={inputClass}
                  disabled={loading || pageLoading}
                  inputMode="decimal"
                  maxLength={6}
                />
              </div>

              {/* Effect Date */}
              <div className="mb-8">
                <label className={labelClass}>
                  Effect Date <span className="text-red-500">*</span>
                </label>

                <input
                  type="date"
                  value={ddmmyyyyToYmd(entryDate)}
                  max={todayYmd}
                  onChange={(e) => setEntryDate(ymdToDDMMYYYY(e.target.value))}
                  className={inputClass}
                  disabled={loading || pageLoading}
                />

                {entryDate ? (
                  <p className={`text-xs mt-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Selected: {entryDate}
                  </p>
                ) : null}
              </div>

              {/* Info */}
              <div className="mb-8">
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Effect date must be current or past date. Percentage should be greater than 0 and less than or equal to 100.
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading || pageLoading}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <FaUndo className="w-4 h-4" />
                  {isEdit ? "Reload" : "Clear"}
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || pageLoading || !canSave}
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading || pageLoading || !canSave
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      {isEdit ? "Update" : "Save"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE SPARE DEDUCTION PERCENT
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSpareDeductionEntry;